import React from "react";
import { useAppContext } from "../context/AppContext";
import { Link } from "react-router-dom";

const TruckList = () => {
  const { trucks, loading, error } = useAppContext();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-600">Loading trucks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg font-semibold">Error loading trucks</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!trucks || trucks.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">No trucks available</p>
      </div>
    );
  }

  const displayedTrucks = trucks.slice(0, 6);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-10 text-center">
          Our Vehicles
        </h1>

        {/* Trucks Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedTrucks.map((truck) => (
            <Link
              key={truck.id}
              to={`/trucks/${truck.slug}`}
              className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 flex flex-col"
            >
              {/* Truck Image */}
              <div className="h-40 bg-gray-100 overflow-hidden">
                {truck.image_url ? (
                  <img
                    src={truck.image_url}
                    alt={truck.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    No Image
                  </div>
                )}
              </div>

              {/* Card Body */}
              <div className="flex flex-col flex-1 p-4">
                {/* Category */}
                <span className="inline-block bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2 w-fit">
                  {truck.category || "Truck"}
                </span>

                {/* Name (truncate to 1 line) */}
                <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1 group-hover:text-red-600 transition-colors">
                  {truck.name}
                </h3>

                {/* Small Description (truncate to 2 lines) */}
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {truck.small_description || "No description available."}
                </p>

                {/* Feet */}
                {truck.feets && (
                  <p className="text-xs text-gray-500 mb-3">
                    <span className="font-semibold">Feet:</span> {truck.feets}
                  </p>
                )}

                {/* Divider */}
                <hr className="border-gray-200 my-3" />

                {/* Key Specs */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    Key Specs:
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>
                      <span className="font-semibold">Model:</span>{" "}
                      {truck.specifications?.model || "N/A"}
                    </li>
                    <li>
                      <span className="font-semibold">Engine:</span>{" "}
                      {truck.specifications?.engine || "N/A"}
                    </li>
                    <li>
                      <span className="font-semibold">Wheelbase:</span>{" "}
                      {truck.specifications?.wheelbase || "N/A"}
                    </li>
                  </ul>
                </div>

                {/* Spacer */}
                <div className="flex-grow"></div>

                {/* View Details (inside link) */}
                <div className="mt-auto">
                  <div className="w-full text-center bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors duration-200">
                    View Details
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* See All Vehicles Button */}
        {trucks.length > 6 && (
          <div className="text-center mt-10">
            <Link
              to="/sales"
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200"
            >
              See All Vehicles
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default TruckList;
